import { useAngularContext } from 'AngularContext';
import { useStudentDashboardContent } from 'StudentDashboardContent';
import { type ContentAccessHelperClass } from 'Lessons';
import { useMemo } from 'react';
import { type PlaylistClass, type Playlist } from './Playlists.types';
import { playlistToIguanaAttrs } from './playlist';

// Eventually when we refactor these hooks to use RTKQuery, we can use the localePackId and localePackIds arguments
// to filter the results via `selectFromResult` a la:
//
// api.useGetPlaylistQuery(undefined, {
//  selectFromResult: ({ data }) => ({
//      playlist: data?.find((playlist) => playlist.localePackId === localePackId),
//    }),
//  })
//
// See also: https://redux-toolkit.js.org/rtk-query/usage/queries#selecting-data-from-a-query-result
//
// When we do this, we should also think about if it makes sense to expose these hooks outside of the current User / learner context.
// We'll be removing the dependency on useStudentDashboardContent / useLearnerContentCache, which implicitly depend on the current User.
// We could theoretically moved to a world where we:
//  1. Have a `usePlaylists` hook to get the playlists to display on the user's dashboard,
//  2. Have a `usePlaylist` hook to get a single (active?) Playlist's information, which calls into
//     the `useStreams` hook to get the Playlist's Stream's information.
//
// This would put us in a world where we have more HTTP requests but where we're also querying and caching individual Playlists and Streams
// in RTKQuery, which makes sense because we don't need information about EVERY Stream while in the Playlist map in the dashboard. We only need
// information about the Playlists the user should see when viewing that UI. Then when they have an active Playlist or click into a Playlist to
// make it active, we can use `usePlaylist` and `useStreams`.
export function usePlaylist(localePackId: string) {
    return usePlaylists([localePackId])[0];
}

/** The playlists returned are in the same order as `localePackIds`. */
export function usePlaylists(localePackIds: string[]) {
    const { availablePlaylistsByLocalePackId } = useStudentDashboardContent();

    return useMemo(
        () =>
            localePackIds
                .map(localePackId => availablePlaylistsByLocalePackId[localePackId])
                .filter((playlist): playlist is Playlist => !!playlist),
        [localePackIds, availablePlaylistsByLocalePackId],
    );
}

export const useLaunchablePlaylists = (localePackIds: string[]) => {
    const ContentAccessHelper = useAngularContext().get<ContentAccessHelperClass>('ContentAccessHelper');
    const PlaylistIguanaClass = useAngularContext().get<PlaylistClass>('Playlist');
    const playlists = usePlaylists(localePackIds);

    const iguanaPlaylists = useMemo(
        () =>
            playlists.map(playlist => {
                const cachedPlaylist = PlaylistIguanaClass.getCachedForLocalePackId(playlist.localePackId, false);
                if (cachedPlaylist) return cachedPlaylist;

                return PlaylistIguanaClass.new(playlistToIguanaAttrs(playlist));
            }),
        [playlists, PlaylistIguanaClass],
    );

    return useMemo(
        () => iguanaPlaylists.filter(playlist => ContentAccessHelper.canLaunch(playlist)),
        [iguanaPlaylists, ContentAccessHelper],
    );
};
